import { ComponentProps, useMemo } from 'react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { GetWeavePopNotificationByType } from '@frontend/types';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';
import { getThreadAiNotificationClassification } from '../utils/thread-ai-notification-classification';
import { BaseNotificationComponentProps, BaseNotificationInner, BaseNotificationOuter } from './base';

type Props = BaseNotificationComponentProps<GetWeavePopNotificationByType<'ai-receptionist-state'>>;

export const AiReceptionistStateNotificationComponent = ({ isHistorical, notification, emit, ...rest }: Props) => {
  const { t } = useTranslation('notifications');

  const actions: ComponentProps<typeof BaseNotificationInner>['actions'] = useMemo(
    () => [
      {
        label: t('View'),
        action: () => {
          emit({ action: 'view', payload: undefined }, notification);
        },
      },
    ],
    [t, emit, notification]
  );

  const payload = notification.payload;
  const threadAiActivity = payload.threadAiActivity;
  if (!threadAiActivity) return null;

  const { personPhoneNumber } = threadAiActivity;
  const person = threadAiActivity.person;
  const preferredName = person?.preferredName || '';
  const firstName = person?.firstName || '';
  const lastName = person?.lastName || '';
  const fullName = `${firstName} ${lastName}`.trim();
  const displayName = preferredName || fullName || (personPhoneNumber ? formatPhoneNumber(personPhoneNumber) : '');
  const notificationClassification = getThreadAiNotificationClassification(threadAiActivity);
  const hasRelatedTask = notificationClassification?.category === 'needs-attention' && !!threadAiActivity.relatedTaskId;

  return (
    <BaseNotificationOuter
      actions={actions}
      notification={notification}
      emit={emit}
      isHistorical={isHistorical}
      {...rest}
    >
      <div
        css={{
          '> div > header': {
            marginBottom: theme.spacing(2),
          },
        }}
      >
        <BaseNotificationInner
          onClose={() => emit({ action: 'ignore', payload: undefined }, notification)}
          actions={actions}
          title={t('Messaging Event by AI Receptionist')}
          body={
            <section
              css={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: theme.spacing(1),
              }}
            >
              {notificationClassification && (
                <div
                  css={{
                    display: 'flex',
                    gap: theme.spacing(0.5),
                    alignItems: 'center',
                  }}
                >
                  <span
                    css={
                      notificationClassification.customIconColor
                        ? {
                            color: notificationClassification.customIconColor,
                          }
                        : undefined
                    }
                  >
                    <Icon
                      name={notificationClassification.iconName}
                      size={16}
                      color={
                        notificationClassification.customIconColor ? undefined : notificationClassification.iconColor
                      }
                      css={
                        notificationClassification.category === 'needs-attention'
                          ? {
                              borderRadius: theme.borderRadius.small,
                              background: `linear-gradient(100deg, ${theme.colors.warning10} 0%, ${theme.colors.warning5} 100%)`,
                              svg: {
                                color: theme.colors.warning80,
                              },
                            }
                          : undefined
                      }
                    />
                  </span>
                  <Text
                    size='large'
                    weight='bold'
                    css={{
                      margin: 0,
                    }}
                  >
                    {notificationClassification.label}
                  </Text>
                </div>
              )}
              {displayName && <Text>{displayName}</Text>}
              {hasRelatedTask && (
                <div
                  css={{
                    display: 'flex',
                    gap: theme.spacing(0.5),
                    alignItems: 'center',
                    color: theme.colors.tangerine50,
                  }}
                >
                  <Icon name='task-created-ai' size={14} />
                  <Text size='medium' css={{ color: theme.colors.tangerine50 }}>
                    {t('Task Created!')}
                  </Text>
                </div>
              )}
            </section>
          }
          location={notification.location}
          timestamp={notification.timestamp}
          id={notification.id}
          notificationType={notification.type}
          {...rest}
        />
      </div>
    </BaseNotificationOuter>
  );
};

export const ThreadAiStateNotificationComponent = AiReceptionistStateNotificationComponent;
